import { Api } from "@core/api/api";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { PersonDto, personFromDto, ProfileDto, profileFromDto } from "./dtos";
import { Profile } from "./profile";
import { Person } from "./user";

const USERS_URL = "/users";
export class UserApi {
	constructor(private readonly api: Api) {}

	async get(id: string): Promise<Person> {
		const result = await this.api.get<Person>(`${USERS_URL}/${id}`);
		return result.data;
	}

	async getProfile(id: string): Promise<Profile> {
		const result = await this.api.get<ProfileDto>(`${USERS_URL}/${id}/profile`);
		return profileFromDto(result.data);
	}
	async getProfileBySlug(slug: string): Promise<Profile> {
		const result = await this.api.get<ProfileDto>(`${USERS_URL}?slug=${slug}`);
		return profileFromDto(result.data);
	}

	async getFollowers(id: string, pageOptions: PaginationOptions): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(`${USERS_URL}/${id}/followers?${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async getFollowees(id: string, pageOptions: PaginationOptions): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(`${USERS_URL}/${id}/followees?${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}
	async disinterestUser(id: string): Promise<void> {
		return this.api.post(`${USERS_URL}/${id}/disinterest`);
	}
	async stopdisinterest(id: string): Promise<void> {
		return this.api.post(`${USERS_URL}/${id}/stopdisinterest`);
	}

	follow(id: string): Promise<void> {
		return this.api.post(`${USERS_URL}/${id}/follow`);
	}

	unfollow(id: string): Promise<void> {
		return this.api.post(`${USERS_URL}/${id}/unfollow`);
	}

	async suggest(pageOptions: PaginationOptions, categories: Category[]): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`${USERS_URL}/suggested?${pageParams(pageOptions)}&categories=${categories.join(",")}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async search(
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		levels?: ContentLevel[],
		languages?: ContentLanguage[],
		durations?: CourseDuration[],
		providers?: CourseProvider[],
		orgTypes?: OrganizationType[],
		orgSubTypes?: OrganizationSubType[],
		countries?: CountryCode[]
	): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`/users?query=${query}&${pageParams(pageOptions)}&${filterParams(
				categories,
				levels,
				languages,
				durations,
				providers,
				orgTypes,
				orgSubTypes,
				countries
			)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async searchUsersWithExclusion(
		pageOptions: PaginationOptions,
		query: string,
		groupId?: string,
		organizationId?: string
	): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`/users?query=${query}&excludeGroupMembers=${groupId}&excludeOrganizationMembers=${organizationId}&${pageParams(
				pageOptions
			)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async searchUsersWithInclusion(
		pageOptions: PaginationOptions,
		query: string,
		organizationId?: string
	): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`/users?query=${query}&includeOrganizationMembers=${organizationId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async searchPersons(query: string, pageOptions: PaginationOptions): Promise<Person[]> {
		const result = await this.api.get<Page<PersonDto>>(`/persons?query=${query}&${pageParams(pageOptions)}`);
		return result.data.content.map(personFromDto);
	}
}
